// utils/multilingualHelpers.js
// Sichere Fallback-Logik für mehrsprachige Programmdaten

/**
 * Holt den Programmnamen in der gewünschten Sprache mit sicheren Fallbacks
 * @param {Object} program - Das Programmobjekt
 * @param {string} language - Gewünschte Sprache ('de', 'en', 'es', 'it')
 * @returns {string} Der Programmname
 */
export const getDisplayName = (program, language = 'de') => {
  if (!program) return '';
  
  // 1. Priorität: Neue mehrsprachige Struktur
  if (program.name && typeof program.name === 'object') {
    return program.name[language] || 
           program.name.de || 
           program.name.en || 
           program.name.es || 
           program.name.it || 
           '';
  }
  
  // 2. Fallback: Alte Struktur
  return program.Originaltitel || 
         program.title || 
         program.name || 
         program.id || 
         'Unbekanntes Programm';
};

/**
 * Holt die Programmbeschreibung in der gewünschten Sprache mit sicheren Fallbacks
 * @param {Object} program - Das Programmobjekt
 * @param {string} language - Gewünschte Sprache ('de', 'en', 'es', 'it')
 * @returns {string} Die Programmbeschreibung
 */
export const getDisplayDescription = (program, language = 'de') => {
  if (!program) return '';
  
  // 1. Priorität: Neue mehrsprachige Struktur
  if (program.description && typeof program.description === 'object') {
    return program.description[language] || 
           program.description.de || 
           program.description.en || 
           program.description.es || 
           program.description.it || 
           '';
  }
  
  // 2. Fallback: Alte Struktur
  return program.Kombinierbarkeit || 
         program.description || 
         program.summary || 
         'Keine Beschreibung verfügbar';
};

/**
 * Prüft, ob ein Programm bereits vollständig übersetzt ist
 * @param {Object} program - Das Programmobjekt
 * @param {string} language - Zu prüfende Sprache
 * @returns {boolean} True wenn übersetzt, false wenn Fallback verwendet wird
 */
export const isTranslated = (program, language = 'de') => {
  if (!program || language === 'de') return true; // Deutsch ist immer verfügbar
  
  const hasTranslatedName = program.name?.[language] && program.name[language].trim() !== '';
  const hasTranslatedDesc = program.description?.[language] && program.description[language].trim() !== '';
  
  return hasTranslatedName && hasTranslatedDesc;
};

/**
 * Holt alle verfügbaren Sprachen für ein Programm
 * @param {Object} program - Das Programmobjekt
 * @returns {Array} Array der verfügbaren Sprachcodes
 */
export const getAvailableLanguages = (program) => {
  if (!program) return ['de'];
  
  const languages = ['de']; // Deutsch ist immer verfügbar
  
  if (program.name && typeof program.name === 'object') {
    ['en', 'es', 'it'].forEach(lang => {
      if (program.name[lang] && program.name[lang].trim() !== '') {
        languages.push(lang);
      }
    });
  }
  
  return languages;
};

/**
 * Debug-Funktion: Zeigt den Migrationsstatus eines Programms
 * @param {Object} program - Das Programmobjekt
 * @returns {Object} Status-Objekt
 */
export const getMigrationStatus = (program) => {
  if (!program) return { migrated: false, reason: 'No program provided' };
  
  const hasNewStructure = program.name && typeof program.name === 'object' && 
                         program.description && typeof program.description === 'object';
  
  const hasOldStructure = program.Originaltitel || program.Kombinierbarkeit;
  
  return {
    migrated: hasNewStructure,
    hasOldStructure,
    hasNewStructure,
    languages: hasNewStructure ? getAvailableLanguages(program) : ['de'],
    fallbackUsed: !hasNewStructure && hasOldStructure
  };
};